import { Col, Row } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import ClientContext from "../../../context/clients/client/context";
import { AreaStatus } from "../../../models/area/enum";
import { IArea } from "../../../models/area/response";
import { ICity } from "../../../models/city/response";
import { IArea as IDiscountArea } from "../../../models/discount-areas/response";
import { getUserStatus } from "../../../models/user/enum";
import EndPoints from "../../../services/end-points";
import { INITIAL_PAGE } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import Form from "../../form-components/from";
import { addSearchToURL, filtersToQueryString, getFiltersFromURL, getKeywordFromURL } from "../../../utils/helpers/url";
import { useLocation } from "react-router-dom";

interface IProps { }

const hasFilterValue = (value: any) =>
  value !== undefined && value !== null && value !== "";

const toNumberFilterValue = (value: any) =>
  hasFilterValue(value) ? Number(value) : undefined;

const activeAreaFilters = [
  {
    name: "status",
    operation: "eq",
    value: AreaStatus.ACTIVE,
  },
];

const ClientFilter: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(ClientContext);

  const statuses = [1, 2];
  const [cities, setCities] = useState<ICity[]>([]);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [areas, setAreas] = useState<IArea[]>([]);
  const [areasLoading, setAreasLoading] = useState<boolean>(false);
  const [discountAreas, setDiscountAreas] = useState<IDiscountArea[]>([]);
  const [discountAreasLoading, setDiscountAreasLoading] = useState<boolean>(false);
  const { direction } = useContext(AppContext);

  const location = useLocation();
    const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  
    useEffect(() => {
      const filters = getFiltersFromURL();
  
      const initialValuesObj: Record<string, any> = {};
      filters.forEach((f) => {
        const isGreaterOrEqual = f.operation === "gte" || f.operation === ">=";
        const isLessOrEqual = f.operation === "lte" || f.operation === "<=";

        if (f.name === "createdAt") {
          const currentRange = initialValuesObj.createdAt ?? ["", ""];
          if (isGreaterOrEqual) currentRange[0] = f.value;
          if (isLessOrEqual) currentRange[1] = f.value;
          initialValuesObj.createdAt = currentRange;
          return;
        }

        if (f.name === "ordersCount") {
          if (isGreaterOrEqual) initialValuesObj.ordersCountMin = f.value;
          if (isLessOrEqual) initialValuesObj.ordersCountMax = f.value;
          return;
        }

        if (f.name === "addressesCount") {
          if (isGreaterOrEqual) initialValuesObj.addressesCountMin = f.value;
          if (isLessOrEqual) initialValuesObj.addressesCountMax = f.value;
          return;
        }

        initialValuesObj[f.name] = f.value;
      });
  
      setInitialValues(initialValuesObj);
    }, [location.search]);
  useEffect(() => {
    const getFilterOptions = async () => {
      setCitiesLoading(true);
      setAreasLoading(true);
      setDiscountAreasLoading(true);
      try {
        const [citiesResponse, areasResponse, discountAreasResponse] = await Promise.all([
          EndPoints.city.getAllCities({
            light: true,
            perPage: -1,
          }),
          EndPoints.area.getAllAreas({
            light: true,
            perPage: -1,
            filters: activeAreaFilters,
          }),
          EndPoints.discountarea.getAllAreas({
            light: true,
            perPage: -1,
            filters: activeAreaFilters,
          }),
        ]);
        setCities(citiesResponse.data.data);
        setAreas(areasResponse.data.data);
        setDiscountAreas(discountAreasResponse.data.data);
      } catch (error) {
      } finally {
        setCitiesLoading(false);
        setAreasLoading(false);
        setDiscountAreasLoading(false);
      }
    };
    getFilterOptions();
  }, [direction]);
  return (
    <Form
      formId="client-filter"
      defaultValues={initialValues}
      onSubmit={(data) => {
        const keyword = getKeywordFromURL();

        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword,
          filters: [
            hasFilterValue(data?.status)
              ? {
                name: "status",
                value: data.status,
                operation: "eq",
              }
              : undefined,

            hasFilterValue(data?.cityId)
              ? {
                name: "cityId",
                value: data.cityId,
                operation: "eq",
              }
              : undefined,
            data?.createdAt != undefined && data.createdAt[0] != ""
              ? {
                name: "createdAt",
                value: data.createdAt[0],
                operation: "gte",
              }
              : undefined,
            data?.createdAt != undefined && data.createdAt[1] != ""
              ? {
                name: "createdAt",
                value: data.createdAt[1],
                operation: "lte",
              }
              : undefined,
            hasFilterValue(data?.ordersCountMin)
              ? {
                name: "ordersCount",
                value: toNumberFilterValue(data.ordersCountMin),
                operation: "gte",
              }
              : undefined,
            hasFilterValue(data?.ordersCountMax)
              ? {
                name: "ordersCount",
                value: toNumberFilterValue(data.ordersCountMax),
                operation: "lte",
              }
              : undefined,
            hasFilterValue(data?.addressesCountMin)
              ? {
                name: "addressesCount",
                value: toNumberFilterValue(data.addressesCountMin),
                operation: "gte",
              }
              : undefined,
            hasFilterValue(data?.addressesCountMax)
              ? {
                name: "addressesCount",
                value: toNumberFilterValue(data.addressesCountMax),
                operation: "lte",
              }
              : undefined,
            hasFilterValue(data?.primaryAreaId)
              ? {
                name: "primaryAreaId",
                value: data.primaryAreaId,
                operation: "eq",
              }
              : undefined,
            hasFilterValue(data?.primaryDiscountAreaId)
              ? {
                name: "primaryDiscountAreaId",
                value: data.primaryDiscountAreaId,
                operation: "eq",
              }
              : undefined,
            hasFilterValue(data?.anyAddressAreaId)
              ? {
                name: "anyAddressAreaId",
                value: data.anyAddressAreaId,
                operation: "eq",
              }
              : undefined,
            hasFilterValue(data?.anyAddressDiscountAreaId)
              ? {
                name: "anyAddressDiscountAreaId",
                value: data.anyAddressDiscountAreaId,
                operation: "eq",
              }
              : undefined,
          ].filter((f) => f),
        };
        const queryString = filtersToQueryString(filter.filters);
        window.history.replaceState(null, "", `?${queryString}`);
        addSearchToURL(keyword);
        actions.setQuery(filter);
      }}
    >
      <Row justify={"start"} gutter={[10, 0]}>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="status"
            label={`${t("status")}`}
            width="large"
            input={{
              type: "select",
              options: statuses.map((status, index) => {
                return {
                  label: t(getUserStatus(status) as string),
                  value: status as number,
                };
              }),
            }}
          />
        </Col>

        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="cityId"
            label={`${t("city")}`}
            width="large"
            input={{
              type: "select",
              loading: citiesLoading,
              options: cities.map((city, index) => {
                return {
                  type: "select",
                  label: city?.name as string,
                  value: city?.id as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <FieldBuilder
            name="createdAt"
            label={`${t("subscription_date")}`}
            input={{ type: "range-picker" }}
            rules={{ required: false }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="ordersCountMin"
            label={`${t("orders_count_from")}`}
            width="large"
            input={{ type: "number" }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="ordersCountMax"
            label={`${t("orders_count_to")}`}
            width="large"
            input={{ type: "number" }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="addressesCountMin"
            label={`${t("addresses_count_from")}`}
            width="large"
            input={{ type: "number" }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="addressesCountMax"
            label={`${t("addresses_count_to")}`}
            width="large"
            input={{ type: "number" }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="primaryAreaId"
            label={`${t("primary_area")}`}
            width="large"
            input={{
              type: "select",
              loading: areasLoading,
              options: areas.map((area) => {
                return {
                  type: "select",
                  label: area?.name as string,
                  value: area?.id as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="primaryDiscountAreaId"
            label={`${t("primary_discount_area")}`}
            width="large"
            input={{
              type: "select",
              loading: discountAreasLoading,
              options: discountAreas.map((area) => {
                return {
                  type: "select",
                  label: area?.name as string,
                  value: area?.id as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="anyAddressAreaId"
            label={`${t("any_address_area")}`}
            width="large"
            input={{
              type: "select",
              loading: areasLoading,
              options: areas.map((area) => {
                return {
                  type: "select",
                  label: area?.name as string,
                  value: area?.id as number,
                };
              }),
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6} xl={6} xxl={6}>
          <FieldBuilder
            name="anyAddressDiscountAreaId"
            label={`${t("any_address_discount_area")}`}
            width="large"
            input={{
              type: "select",
              loading: discountAreasLoading,
              options: discountAreas.map((area) => {
                return {
                  type: "select",
                  label: area?.name as string,
                  value: area?.id as number,
                };
              }),
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default ClientFilter;
